import { canAccessPortalArea } from "@whitehaven/permissions";
import type {
  AuthorizationSubject,
  PortalCapabilityArea,
  Role,
} from "@whitehaven/permissions";
import { ADMIN_HOMEPAGE_CONTENT_PATH } from "./features/content/homepage-editor-route-model.ts";

export type PortalArea = "guest" | "staff" | "admin";
export type NavigationMatch = "exact" | "prefix";
export type NavigationDismissReason =
  | "escape"
  | "selection"
  | "route-change"
  | "desktop-breakpoint"
  | "logout";

export interface NavigationLink {
  readonly id: string;
  readonly label: string;
  readonly href: string;
  readonly match: NavigationMatch;
}

export interface ProtectedNavigationItem extends NavigationLink {
  readonly area: PortalArea;
  readonly capabilityArea: PortalCapabilityArea;
}

export interface MobileNavigationState {
  readonly open: boolean;
}

export type MobileNavigationAction =
  | { readonly type: "toggle" }
  | { readonly type: "open" }
  | {
      readonly type: "dismiss";
      readonly reason: NavigationDismissReason;
    };

export const NAVIGATION_MEDIA_QUERY = "(max-width: 900px)";

const areaRoles: Readonly<Record<PortalArea, readonly Role[]>> = {
  admin: ["OWNER", "ADMIN"],
  staff: ["SCHEDULER", "FRONT_DESK", "STAFF", "CRM_AGENT"],
  guest: ["GUEST"],
};

const areaPriority: readonly PortalArea[] = ["admin", "staff", "guest"];

export const portalAreaLabels: Readonly<Record<PortalArea, string>> = {
  admin: "Admin",
  staff: "Staff",
  guest: "Guest",
};

export const protectedNavigationItems: readonly ProtectedNavigationItem[] = [
  {
    id: "guest-overview",
    area: "guest",
    label: "Overview",
    href: "/guest",
    capabilityArea: "guest-overview",
    match: "exact",
  },
  {
    id: "guest-profile",
    area: "guest",
    label: "Profile",
    href: "/guest/profile",
    capabilityArea: "guest-profile",
    match: "prefix",
  },
  {
    id: "guest-appointments",
    area: "guest",
    label: "Appointments",
    href: "/guest/appointments",
    capabilityArea: "guest-appointments",
    match: "prefix",
  },
  {
    id: "guest-forms",
    area: "guest",
    label: "Forms",
    href: "/guest/forms",
    capabilityArea: "guest-forms",
    match: "prefix",
  },
  {
    id: "guest-messages",
    area: "guest",
    label: "Messages",
    href: "/guest/messages",
    capabilityArea: "guest-messages",
    match: "prefix",
  },
  {
    id: "guest-documents",
    area: "guest",
    label: "Documents",
    href: "/guest/documents",
    capabilityArea: "guest-documents",
    match: "prefix",
  },
  {
    id: "guest-invoices",
    area: "guest",
    label: "Invoices",
    href: "/guest/invoices",
    capabilityArea: "guest-invoices",
    match: "prefix",
  },
  {
    id: "staff-overview",
    area: "staff",
    label: "Overview",
    href: "/staff",
    capabilityArea: "staff-overview",
    match: "exact",
  },
  {
    id: "staff-today",
    area: "staff",
    label: "Today",
    href: "/staff/today",
    capabilityArea: "staff-today",
    match: "prefix",
  },
  {
    id: "staff-schedule",
    area: "staff",
    label: "Schedule",
    href: "/staff/schedule",
    capabilityArea: "staff-schedule",
    match: "prefix",
  },
  {
    id: "staff-check-in",
    area: "staff",
    label: "Check in",
    href: "/staff/check-in",
    capabilityArea: "staff-check-in",
    match: "prefix",
  },
  {
    id: "staff-checkout",
    area: "staff",
    label: "Checkout",
    href: "/staff/checkout",
    capabilityArea: "staff-checkout",
    match: "prefix",
  },
  {
    id: "staff-print",
    area: "staff",
    label: "Print",
    href: "/staff/print",
    capabilityArea: "staff-print",
    match: "prefix",
  },
  {
    id: "staff-crm",
    area: "staff",
    label: "CRM",
    href: "/admin/crm",
    capabilityArea: "staff-crm",
    match: "prefix",
  },
  {
    id: "staff-conversations",
    area: "staff",
    label: "Conversations",
    href: "/admin/conversations",
    capabilityArea: "staff-conversations",
    match: "exact",
  },
  {
    id: "admin-crm",
    area: "admin",
    label: "CRM",
    href: "/admin/crm",
    capabilityArea: "admin-crm",
    match: "prefix",
  },
  {
    id: "admin-homepage-content",
    area: "admin",
    label: "Homepage",
    href: ADMIN_HOMEPAGE_CONTENT_PATH,
    capabilityArea: "admin-homepage",
    match: "exact",
  },
  {
    id: "admin-conversations",
    area: "admin",
    label: "Conversations",
    href: "/admin/conversations",
    capabilityArea: "admin-conversations",
    match: "exact",
  },
  {
    id: "admin-calendar",
    area: "admin",
    label: "Calendar",
    href: "/admin/calendar",
    capabilityArea: "admin-calendar",
    match: "prefix",
  },
  {
    id: "admin-scheduler",
    area: "admin",
    label: "Scheduler",
    href: "/admin/scheduler",
    capabilityArea: "admin-scheduler",
    match: "prefix",
  },
  {
    id: "admin-appointments",
    area: "admin",
    label: "Appointments",
    href: "/admin/appointments",
    capabilityArea: "admin-appointments",
    match: "prefix",
  },
  {
    id: "admin-resources",
    area: "admin",
    label: "Resources",
    href: "/admin/resources",
    capabilityArea: "admin-resources",
    match: "prefix",
  },
  {
    id: "admin-guests",
    area: "admin",
    label: "Guests",
    href: "/admin/guests",
    capabilityArea: "admin-guests",
    match: "prefix",
  },
  {
    id: "admin-documents",
    area: "admin",
    label: "Documents",
    href: "/admin/documents",
    capabilityArea: "admin-documents",
    match: "prefix",
  },
  {
    id: "admin-invoices",
    area: "admin",
    label: "Billing",
    href: "/admin/invoices",
    capabilityArea: "admin-invoices",
    match: "prefix",
  },
  {
    id: "admin-roles",
    area: "admin",
    label: "Roles",
    href: "/admin/roles",
    capabilityArea: "admin-roles",
    match: "prefix",
  },
  {
    id: "admin-audit",
    area: "admin",
    label: "Audit",
    href: "/admin/audit",
    capabilityArea: "admin-audit",
    match: "prefix",
  },
  {
    id: "admin-settings",
    area: "admin",
    label: "Settings",
    href: "/admin/settings",
    capabilityArea: "admin-settings",
    match: "prefix",
  },
  {
    id: "admin-site-setup",
    area: "admin",
    label: "Site setup",
    href: "/admin/site-setup",
    capabilityArea: "admin-site-setup",
    match: "prefix",
  },
];

export const publicNavigationItems: readonly NavigationLink[] = [
  {
    id: "portal-home",
    label: "Portal home",
    href: "/",
    match: "exact",
  },
  {
    id: "public-website",
    label: "Public website",
    href: "/",
    match: "exact",
  },
  {
    id: "privacy",
    label: "Privacy",
    href: "/privacy",
    match: "exact",
  },
];

function hasAreaRole(subject: AuthorizationSubject, area: PortalArea) {
  return areaRoles[area].some((role) => subject.roles.includes(role));
}

export function resolvePortalArea(
  subject: AuthorizationSubject | null,
): PortalArea | null {
  if (!subject) return null;
  return areaPriority.find((area) => hasAreaRole(subject, area)) ?? null;
}

export function getPortalAreaLabel(
  subject: AuthorizationSubject | null,
  area: PortalArea,
) {
  const isCrmOnly =
    area === "staff" &&
    subject?.roles.includes("CRM_AGENT") &&
    !subject.roles.some((role) =>
      ["SCHEDULER", "FRONT_DESK", "STAFF"].includes(role),
    );

  return isCrmOnly ? "CRM" : portalAreaLabels[area];
}

export function visibleNavigationItems(
  subject: AuthorizationSubject | null,
  _area: PortalArea | null = resolvePortalArea(subject),
) {
  if (!subject) return [];

  return protectedNavigationItems.filter((item) =>
    canAccessPortalArea(subject, item.capabilityArea, "view"),
  );
}

function normalizedPath(pathname: string) {
  const path = pathname.split(/[?#]/, 1)[0] || "/";
  return path.length > 1 ? path.replace(/\/+$/, "") : path;
}

export function isNavigationItemActive(item: NavigationLink, pathname: string) {
  if (!item.href.startsWith("/")) return false;

  const current = normalizedPath(pathname);
  const destination = normalizedPath(item.href);
  if (item.match === "exact") return current === destination;
  return current === destination || current.startsWith(`${destination}/`);
}

export function reduceMobileNavigation(
  state: MobileNavigationState,
  action: MobileNavigationAction,
): MobileNavigationState {
  if (action.type === "toggle") return { open: !state.open };
  if (action.type === "open") return { open: true };
  return { open: false };
}

export function shouldRestoreNavigationTrigger(
  reason: NavigationDismissReason,
) {
  return reason === "escape";
}
